import type {
  AiUseEntry,
  AuditEvent,
  CalcRun,
  CalcStep,
  Corridor,
  CorridorException,
  Engagement,
  Entity,
  ExternalValuation,
  Holding,
  Hypothesis,
  Instrument,
  InstrumentClass,
  OptionTranche,
  ShareClass,
  InstrumentClassification,
  InstrumentMarketInput,
  InstrumentTier,
  Role,
  StageGate,
  Tenant,
  ValuationReport,
  ValuationUnit,
} from '@ave/shared';

export interface ApiFailure {
  status: number;
  error: string;
  message: string;
  detail?: unknown;
  launchUrl?: string;
}

export class ApiError extends Error implements ApiFailure {
  status: number;
  error: string;
  detail?: unknown;
  launchUrl?: string;

  constructor(failure: ApiFailure) {
    super(failure.message);
    this.status = failure.status;
    this.error = failure.error;
    this.detail = failure.detail;
    this.launchUrl = failure.launchUrl;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // The launch cookie is the session. Every call must carry it.
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError({
      status: res.status,
      error: String(body.error ?? 'request_failed'),
      message: String(body.message ?? `Request failed (${res.status})`),
      detail: body.detail,
      launchUrl: body.launchUrl as string | undefined,
    });
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface Me {
  user: {
    id: string;
    email: string;
    name: string;
    role: Role;
    licenceReadOnly: boolean;
    plan: string;
  };
  tenant: Tenant;
  tool: { id: string; label: string };
}

export type EngagementSummary = Engagement & { holdingCount: number; openExceptions: number };

export type CalcRunWithSteps = CalcRun & { steps: CalcStep[] };

export interface EmiRestriction {
  label: string;
  discount: string;
  justification?: string | null;
}

export interface EmiDetails {
  id: string;
  entityId: string | null;
  shareClass: string;
  grantDate: string | null;
  sharesUnderOption: string | null;
  exercisePricePerShare: string | null;
  restrictions: EmiRestriction[];
  grants: { employee_ref: string; shares: string }[];
  companyUmvAlreadyCommitted: string;
  articlesReference: string | null;
  notes: string | null;
  updatedAt: string;
}

export interface AllotmentReading {
  id: string;
  entityId: string;
  filingType: string;
  filingTransactionId: string | null;
  filingDate: string | null;
  documentUrl: string | null;
  shareClass: string;
  allotmentDate: string | null;
  shares: string | null;
  pricePerShare: string | null;
  currency: string | null;
  considerationKind: string;
  considerationDescription: string | null;
  confidence: number | null;
  holdReasons: string[];
  promptTemplate: string | null;
  status: 'held' | 'confirmed' | 'dismissed';
  resolutionNote: string | null;
  transactionId: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface InterviewState {
  session: { id: string; status: string; createdAt: string } | null;
  messages: {
    id: string;
    role: 'agent' | 'participant' | 'system';
    speaker: string | null;
    body: string;
    hypothesisRef: string | null;
    createdAt: string;
  }[];
  findings: {
    id: string;
    claim: string;
    provenance: string;
    supportingQuote: string | null;
  }[];
  hypotheses: Hypothesis[];
}

export interface SotpState {
  units: {
    unitId: string;
    name: string;
    value: string;
    method: string;
    runId: string;
    functionName: string;
  }[];
  /** Units with no calculation behind them. A bridge is refused until empty. */
  unvalued: string[];
  latestBridge: { id: string; outputs: Record<string, unknown>; createdAt: string } | null;
}

export interface AdminCorridor {
  id: string;
  assumptionKey: string;
  module: string;
  purpose: string | null;
  derivation: 'curve_derived' | 'evidence_range' | 'fixed_band';
  params: Record<string, unknown>;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface StatutoryAmount {
  id: string;
  amountKey: string;
  label: string;
  amount: string;
  currency: string;
  citation: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** A figure or assumption as the reading found it, with where it came from. */
export interface PensionReadItem {
  key: string;
  label: string;
  value: string;
  unit?: 'ratio' | 'years' | 'text';
  basis?: string | null;
  page?: number | null;
  source_text?: string | null;
  confidence: number;
}

export interface PensionReading {
  id: string;
  documentId: string;
  schemeName: string | null;
  sponsorName: string | null;
  measurementDate: string | null;
  reportKind: string | null;
  basis: string | null;
  currency: string | null;
  figures: PensionReadItem[];
  assumptions: PensionReadItem[];
  uncertain: { key: string; reason: string }[];
  notes: string | null;
  promptTemplate: string | null;
  requiresHumanConfirmation: boolean;
  status: 'proposed' | 'confirmed' | 'dismissed';
  resolutionNote: string | null;
  positionId: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface PensionPosition {
  id: string;
  entityId: string | null;
  schemeName: string;
  measurementDate: string;
  basis: string;
  currency: string;
  definedBenefitObligation: string;
  fairValueOfAssets: string;
  netPosition: string;
  assumptions: { key: string; label: string; value: string; unit: string }[];
  provenance: string;
  actuaryName: string | null;
  reportReference: string | null;
  sourceDocumentId: string | null;
  checked: string;
  confirmedAt: string;
}

export interface ModelPrice {
  id: string;
  model: string;
  inputPerMillion: string;
  outputPerMillion: string;
  currency: string;
  citation: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** `costUsd` is null where no call in the row could be priced at all. */
export interface UsageByJob {
  engagementId: string | null;
  engagementLabel: string;
  userId: string | null;
  userLabel: string;
  calls: number;
  tokensInput: number;
  tokensOutput: number;
  unreported: number;
  unpriced: number;
  costUsd: number | null;
  lastCallAt: string;
}

export interface UsageByModel {
  model: string;
  calls: number;
  tokensInput: number;
  tokensOutput: number;
  unpriced: number;
  notOk: number;
  costUsd: number | null;
}

export interface UsageReportRow {
  id: string;
  engagementRef: string | null;
  calls: number;
  tokensInput: number;
  tokensOutput: number;
  costUsd: number | null;
  unpricedCalls: number;
  status: 'sent' | 'failed' | 'dormant';
  detail: string | null;
  createdAt: string;
}

export interface UsagePushOutcome {
  status: 'sent' | 'failed' | 'dormant';
  batches: number;
  calls: number;
  costUsd: number;
  unpricedCalls: number;
  detail?: string;
}

export interface CapTableForEntity {
  entityId: string;
  classes: ShareClass[];
  pool: OptionTranche[];
  latestAllocation: CalcRun | null;
}

export interface EngagementDocument {
  id: string;
  engagementId: string;
  filename: string;
  mimeType: string | null;
  byteSize: number | null;
  storageKey: string;
  sha256: string | null;
  kind: string;
  provenance: string;
  createdAt: string;
}

export interface EngagementDetail {
  engagement: Engagement;
  entities: Entity[];
  units: ValuationUnit[];
  instruments: Instrument[];
  instrumentClassifications: InstrumentClassification[];
  holdings: Holding[];
  externals: ExternalValuation[];
  exceptions: CorridorException[];
  shareTransactions: {
    id: string;
    txnDate: string;
    shareClass: string;
    pricePerShare: string;
    shares: string | null;
    counterpartyRelationship: string;
    /** What was given for the shares; only `cash` makes the price evidence of value on its own. */
    considerationKind: string;
    /** Where the price was read from — a Companies House document URL for a filed form. */
    sourceReference: string | null;
    provenance: string;
    notes: string | null;
  }[];
  /** The EMI scheme facts behind the VAL231 pack. Null until recorded. */
  emiDetails?: EmiDetails | null;
  /**
   * Lines from a filed allotment form that were read but not written as
   * evidence, with the reasons, waiting for a preparer. `pricePerShare` is
   * what the model read — shown so it can be checked against the form, never
   * computed with.
   *
   * Optional because the services deploy serially: for the length of a
   * promotion the web is newer than the API, and a panel that assumed the
   * field would blank the tab.
   */
  allotmentReadings?: AllotmentReading[];
  findings: {
    id: string;
    claim: string;
    provenance: string;
    supportingQuote: string | null;
    evidenceRefs: string[];
  }[];
  runs: (CalcRun & { steps: CalcStep[] })[];
  gates: StageGate[];
  evidencePoints: {
    id: string;
    metric: string;
    value: string;
    unit: string | null;
    sectorCode: string | null;
    asAt: string;
    sourceName: string;
    quality: string;
  }[];
  corridors: Corridor[];
  reports: ValuationReport[];
}

export const api = {
  me: () => request<Me>('/api/me'),
  health: () => request<{ status: string; driver: string; calc: string }>('/api/health'),

  listEngagements: () => request<{ engagements: EngagementSummary[] }>('/api/engagements'),

  getEngagement: (id: string) => request<EngagementDetail>(`/api/engagements/${id}`),

  createEngagement: (body: Record<string, unknown>) =>
    request<{ engagement: Engagement }>('/api/engagements', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createEntity: (id: string, body: Record<string, unknown>) =>
    request<{ entity: Entity }>(`/api/engagements/${id}/entities`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createHolding: (id: string, body: Record<string, unknown>) =>
    request<{ holding: Holding }>(`/api/engagements/${id}/holdings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createValuationUnit: (id: string, body: Record<string, unknown>) =>
    request<{ unit: ValuationUnit }>(`/api/engagements/${id}/valuation-units`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Correct a unit. Redefining what it *is* — its type, its split criterion,
   * whether it is separable — withdraws the methodology's approval, and the
   * response says so; renaming it does not.
   */
  updateValuationUnit: (unitId: string, body: Record<string, unknown>) =>
    request<{ unit: ValuationUnit; approvalWithdrawn: boolean }>(
      `/api/valuation-units/${unitId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  removeValuationUnit: (unitId: string) =>
    request<{ removed: boolean }>(`/api/valuation-units/${unitId}`, { method: 'DELETE' }),

  /**
   * Propose the methodology for one unit. The route hangs off the unit rather
   * than the engagement because a methodology belongs to a unit — and it
   * upserts, so re-proposing replaces the previous one and clears its
   * approval, which is why the screen calls this "change" on a unit that
   * already has one.
   */
  setMethodology: (unitId: string, body: Record<string, unknown>) =>
    request<{ methodology: { id: string; method: string } }>(
      `/api/valuation-units/${unitId}/methodology`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  approveMethodology: (unitId: string) =>
    request<{ approved: boolean }>(`/api/valuation-units/${unitId}/methodology/approve`, {
      method: 'POST',
    }),

  createInstrument: (id: string, body: Record<string, unknown>) =>
    request<{ instrument: Instrument }>(`/api/engagements/${id}/instruments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Price an instrument. The terms come off the record; the market inputs go
   * with the run, because they are evidence somebody chose rather than
   * anything the paperwork said.
   */
  priceInstrument: (
    id: string,
    instrumentId: string,
    marketInputs: { key: InstrumentMarketInput; value: string; evidenceRefs?: string[] }[],
  ) =>
    request<{ instrument: Instrument; run: CalcRun; approvalWithdrawn: boolean }>(
      `/api/engagements/${id}/instruments/${instrumentId}/price`,
      { method: 'POST', body: JSON.stringify({ marketInputs }) },
    ),

  approveInstrumentPrice: (id: string, instrumentId: string) =>
    request<{ instrument: Instrument }>(
      `/api/engagements/${id}/instruments/${instrumentId}/approve`,
      { method: 'POST' },
    ),

  /**
   * Read instrument paperwork into a proposal.
   *
   * Returns a classification, never an instrument. The reading proposes the
   * tier, and the tier is what decides whether the eventual price needs a
   * reviewer — so nothing is recorded until a person confirms it.
   */
  /**
   * Reference data. Both routes return every version rather than only what
   * is in force: an admin about to move a corridor needs to see what it has
   * been, and so does anybody asking why a 2024 valuation used another band.
   */
  /**
   * The discovery interview. The hypotheses come back with it because they
   * are what the agent is meant to be asking about, and a valuer can only
   * tell whether it did if they are on the same screen.
   */
  interview: (id: string) => request<InterviewState>(`/api/engagements/${id}/interview`),

  interviewTurn: (
    id: string,
    body: { message?: string; participantRole?: string },
  ) =>
    request<{
      reply: string;
      findings: number;
      proposedUnits: {
        name: string;
        activity_description: string;
        split_criterion: string;
        rationale: string;
      }[];
      openHypotheses: string[];
      nextTopic: string | null;
      sweepComplete: boolean;
      complete: boolean;
    }>(`/api/engagements/${id}/interview`, { method: 'POST', body: JSON.stringify(body) }),

  closeInterview: (id: string, note: string) =>
    request<{ closed: boolean }>(`/api/engagements/${id}/interview/close`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  /**
   * What would go into a sum-of-the-parts bridge. Each part is the figure its
   * own calculation produced — nothing on that screen is typed in twice.
   */
  sotp: (id: string) => request<SotpState>(`/api/engagements/${id}/sotp`),

  sotpBridge: (
    id: string,
    body: {
      centralCosts: { alreadyInUnitValues: true } | { annual: string; multiple: string; basis?: string };
      groupItems?: { label: string; kind: string; value: string }[];
      groupAdjustment?: { rate: string; basis: string };
    },
  ) =>
    request<{ run: CalcRun }>(`/api/engagements/${id}/sotp-bridge`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminCorridors: () => request<{ corridors: AdminCorridor[] }>('/api/admin/corridors'),

  setCorridor: (body: Record<string, unknown>) =>
    request<{ corridor: AdminCorridor }>('/api/admin/corridors', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  statutoryAmounts: () =>
    request<{ amounts: StatutoryAmount[] }>('/api/admin/statutory-amounts'),

  pensionReadings: (id: string) =>
    request<{ readings: PensionReading[]; positions: PensionPosition[] }>(
      `/api/engagements/${id}/pension-readings`,
    ),

  readPensionReport: (id: string, documentId: string, body: Record<string, unknown> = {}) =>
    request<{ jobId: string | null; queued: boolean; note: string }>(
      `/api/engagements/${id}/documents/${documentId}/read-pension-report`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  confirmPensionReading: (id: string, readingId: string, body: Record<string, unknown>) =>
    request<{ position: PensionPosition }>(
      `/api/engagements/${id}/pension-readings/${readingId}/confirm`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  dismissPensionReading: (id: string, readingId: string, note: string) =>
    request<{ reading: PensionReading }>(
      `/api/engagements/${id}/pension-readings/${readingId}/dismiss`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  modelPrices: () => request<{ prices: ModelPrice[] }>('/api/admin/model-prices'),

  setModelPrice: (body: Record<string, unknown>) =>
    request<{ price: ModelPrice }>('/api/admin/model-prices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminUsage: () =>
    request<{ byJob: UsageByJob[]; byModel: UsageByModel[]; reports: UsageReportRow[] }>(
      '/api/admin/usage',
    ),

  pushUsage: () => request<UsagePushOutcome>('/api/admin/usage/report', { method: 'POST' }),

  aiUse: () =>
    request<{
      boundary: { statement: string; enforcedBy: string };
      entries: AiUseEntry[];
      models: { default: string; escalation: string };
    }>('/api/admin/ai-use'),

  setStatutoryAmount: (body: Record<string, unknown>) =>
    request<{ amount: StatutoryAmount }>('/api/admin/statutory-amounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  capTable: (id: string) =>
    request<{ capTables: CapTableForEntity[] }>(`/api/engagements/${id}/cap-table`),

  createShareClass: (id: string, body: Record<string, unknown>) =>
    request<{ shareClass: ShareClass }>(`/api/engagements/${id}/share-classes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  createOptionTranche: (id: string, body: Record<string, unknown>) =>
    request<{ tranche: OptionTranche }>(`/api/engagements/${id}/option-tranches`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeCapTableRow: (id: string, kind: 'share-classes' | 'option-tranches', rowId: string) =>
    request<{ removed: boolean }>(`/api/engagements/${id}/${kind}/${rowId}`, { method: 'DELETE' }),

  /**
   * Split an equity value across the recorded structure. The classes come
   * off the cap table; the four things the articles do not say go with the
   * request, because they are chosen rather than read.
   */
  allocateEquity: (
    id: string,
    body: {
      entityId: string;
      equityValue: string;
      exitDate: string;
      volatility: string;
      riskFreeRate: string;
      evidenceRefs?: string[];
      valuationUnitId?: string;
    },
  ) =>
    request<{ run: CalcRun }>(`/api/engagements/${id}/opm-allocation`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Store the bytes a valuation was built from. Base64 on a JSON body. */
  uploadDocument: (
    id: string,
    body: {
      filename: string;
      mimeType?: string;
      kind?: string;
      contentBase64: string;
    },
  ) =>
    request<{ document: EngagementDocument }>(`/api/engagements/${id}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  documents: (id: string) =>
    request<{ documents: EngagementDocument[] }>(`/api/engagements/${id}/documents`),

  classifyInstrument: (
    id: string,
    body: {
      entityId: string;
      instrumentName: string;
      holder?: string;
      /** One or the other: what was read has to be one thing. */
      paperwork?: string;
      sourceDocumentId?: string;
    },
  ) =>
    request<{ classification: InstrumentClassification }>(
      `/api/engagements/${id}/instruments/classify`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  confirmClassification: (
    id: string,
    classificationId: string,
    body: {
      note: string;
      name?: string;
      holder?: string;
      cls?: InstrumentClass;
      tier?: InstrumentTier;
      terms?: Record<string, unknown>;
    },
  ) =>
    request<{ instrument: Instrument; classification: InstrumentClassification }>(
      `/api/engagements/${id}/instrument-classifications/${classificationId}/confirm`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  dismissClassification: (id: string, classificationId: string, note: string) =>
    request<{ classification: InstrumentClassification }>(
      `/api/engagements/${id}/instrument-classifications/${classificationId}/dismiss`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  /** The band in force for one assumption at this engagement's valuation date. */
  assumptionCorridor: (id: string, key: string) =>
    request<{
      assumptionKey: string;
      corridor: Corridor | null;
      bounds: { min: number | null; max: number | null };
      valuationDate: string;
    }>(`/api/engagements/${id}/assumptions/${encodeURIComponent(key)}/corridor`),

  recordAssumption: (id: string, body: Record<string, unknown>) =>
    request<{
      assumptionKey: string;
      value: string;
      inCorridor: boolean;
      bounds: { min: number | null; max: number | null };
      evidenceIds: string[];
      exceptionId: string | null;
    }>(`/api/engagements/${id}/assumptions`, { method: 'POST', body: JSON.stringify(body) }),

  /** Record what the EMI scheme is — the shares, the grants, the restrictions. */
  recordEmiDetails: (id: string, body: Record<string, unknown>) =>
    request<{ emiDetails: { id: string } }>(`/api/engagements/${id}/emi-details`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Compute UMV and AMV. Takes no inputs on purpose: the per-share value
   * comes from the calculation log and the statutory limits from the corpus
   * at the valuation date.
   */
  runEmiShareValues: (id: string) =>
    request<{
      run: CalcRun & { steps: CalcStep[] };
      limits: { key: string; amount: string; citation: string }[];
    }>(`/api/engagements/${id}/emi-share-values`, { method: 'POST' }),

  /** Turn a held reading into a transaction. The price is re-entered, never echoed back. */
  confirmAllotmentReading: (id: string, readingId: string, body: Record<string, unknown>) =>
    request<{ readingId: string; transactionId: string; status: 'confirmed' }>(
      `/api/engagements/${id}/allotment-readings/${readingId}/confirm`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** The lead was considered and is not evidence of value. Not a delete. */
  dismissAllotmentReading: (id: string, readingId: string, reason: string) =>
    request<{ readingId: string; status: 'dismissed' }>(
      `/api/engagements/${id}/allotment-readings/${readingId}/dismiss`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  approveStage: (id: string, stage: number, note?: string) =>
    request<{ stage: number; status: string; currentStage: number }>(
      `/api/engagements/${id}/stages/${stage}/approve`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  rejectStage: (id: string, stage: number, note: string, returnToStage?: number) =>
    request<{ stage: number; status: string; currentStage: number }>(
      `/api/engagements/${id}/stages/${stage}/reject`,
      { method: 'POST', body: JSON.stringify({ note, returnToStage }) },
    ),

  approveException: (exceptionId: string, justification: string) =>
    request<{ approved: boolean }>(`/api/corridor-exceptions/${exceptionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ justification }),
    }),

  rejectException: (exceptionId: string, justification: string) =>
    request<{ rejected: boolean }>(`/api/corridor-exceptions/${exceptionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ justification }),
    }),

  runAnalysis: (id: string) =>
    request<{ hypotheses: Hypothesis[] }>(`/api/engagements/${id}/analysis`, { method: 'POST' }),

  draftReport: (id: string) =>
    request<{ report: ValuationReport }>(`/api/engagements/${id}/reports/draft`, {
      method: 'POST',
    }),

  releaseApproval: (id: string, body: Record<string, unknown>) =>
    request<{ decision: string; requiredRole: string }>(`/api/engagements/${id}/release-approvals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  releaseReport: (id: string, reportId: string) =>
    request<{ released: boolean; archiveKey: string; version: number }>(
      `/api/engagements/${id}/reports/${reportId}/release`,
      { method: 'POST' },
    ),

  audit: (id: string) => request<{ events: AuditEvent[] }>(`/api/engagements/${id}/audit`),

  corridors: (asOf?: string) =>
    request<{ corridors: Corridor[] }>(`/api/corridors${asOf ? `?asOf=${asOf}` : ''}`),
};
